<template>
  <v-container fluid>
    <v-container style="max-width: 900px;">
        <v-card>
          <v-card-title class="pt-1 pb-2">
            <span id="admin_config_title" v-translate>Caller Verify Configuration</span>
            <v-spacer></v-spacer>
            <v-text-field
              id="admin_config_search"
              v-model="search"
              append-icon="mdi-magnify"
              label="Search"
              single-line
              hide-details
            ></v-text-field>
            <v-btn
              color="primary"
              class="ml-2 pa-2"
              small
              :disabled="changedSettings.length===0"
              :loading="saveInProgress"
              @click="commit"
            >
              <translate>Save Changes</translate>
            </v-btn>
          </v-card-title>
          <v-data-table
            :headers="headers"
            :items="config"
            :search="search"
            :loading="configLoading"
            group-by="category"
          >
            <template v-slot:top="{pagination, options, updateOptions}">
              <v-data-footer
                :pagination="pagination"
                :options="options"
                @update:options="updateOptions"
                items-per-page-text="$vuetify.dataTable.itemsPerPageText">
              </v-data-footer>
              <v-dialog
                v-model="dialog"
                max-width="500px"
              >
                <v-card>
                  <v-card-title>
                    <span id="cv_edit_config" class="text-h5" v-translate='{config: $cvutils.getTranslatedConfigID(selectedItem.id)}'>Edit: %{ config }</span>
                  </v-card-title>
                  <v-card-subtitle class="ma-2">{{ $cvutils.getTranslatedConfigDesc(selectedItem.id) }}</v-card-subtitle>

                  <v-card-text>
                    <v-container>
                      <v-row
                        v-if="selectedItem.type === 'image'"
                      >
                        <v-img
                          :src=selectedItem.value
                          :max-height="$appConfig.customerLogoHeight"
                          :max-width="$appConfig.customerLogoWidth"
                        >
                        </v-img>
                      </v-row>
                      <v-row justify="center">
                        <v-text-field
                          v-if="selectedItem.type === 'string'"
                          v-model="selectedItem.value"
                        ></v-text-field>
                        <v-switch
                          v-if="selectedItem.type === 'bool'"
                          v-model="selectedItem.value"
                          :label="selectedItem.value?$gettext('Enabled'):$gettext('Disabled')"
                        >
                        </v-switch>
                        <v-color-picker
                          v-if="selectedItem.type === 'color'"
                          v-model="selectedItem.value"
                          mode="hexa"
                        ></v-color-picker>
                        <v-text-field
                          v-if="selectedItem.type === 'image'"
                          v-model="selectedItem.value"
                        ></v-text-field>
                        <v-select
                          v-if="selectedItem.type === 'enum'"
                          v-model="selectedItem.value"
                          :items="getRestrictedValues(selectedItem.id, selectedItem.valid_values)"
                          item-value="value"
                          item-text="display"
                        ></v-select>
                        <v-text-field
                          v-if="selectedItem.type === 'int' && (selectedItem.min == selectedItem.max)"
                          v-model="selectedItem.value"
                          class="mt-0 pt-0"
                          hide-details
                          single-line
                          filled
                          dense
                          rounded
                          type="number"
                          style="width: 100px"
                        ></v-text-field>
                        <v-slider
                          v-if="selectedItem.type === 'int' && (selectedItem.min != selectedItem.max)"
                          v-model="selectedItem.value"
                          :min="selectedItem.min"
                          :max="selectedItem.max"
                          hide-details
                          class="align-center"
                        >
                          <template v-slot:append>
                            <v-text-field
                              v-model="selectedItem.value"
                              class="mt-0 pt-0"
                              hide-details
                              single-line
                              filled
                              dense
                              rounded
                              type="number"
                              style="width: 100px"
                            ></v-text-field>
                          </template>
                        </v-slider>
                        <v-text-field
                          v-if="selectedItem.type === 'password'"
                          v-model="selectedItem.value"
                          :type="showSecret ? 'text' : 'password'"
                        >
                          <template v-slot:append>
                            <!-- control if the caller response input field is starred out or not -->
                            <v-icon @click="showSecret = !showSecret">
                              {{ showSecret ? 'mdi-eye' : 'mdi-eye-off' }}
                            </v-icon>
                          </template>
                        </v-text-field>
                        <EditableList
                          v-if="selectedItem.type === 'list'"
                          v-model="selectedItem.value"
                          :title="selectedItem.name"
                        >
                        </EditableList>
                        <EditableObjectList
                          v-if="selectedItem.type === 'object'"
                          v-model="selectedItem.value"
                          :schema="selectedItem.schema"
                          :config="selectedItem.id"
                          :title="selectedItem.name"
                        >
                        </EditableObjectList>
                      </v-row>
                    </v-container>
                  </v-card-text>

                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                      color="success"
                      @click="update"
                    >
                      Update
                    </v-btn>
                    <v-btn
                      color="primary"
                      @click="close"
                    >
                      Cancel
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </template>
            <!-- eslint-disable-next-line vue/valid-v-slot -->
            <template v-slot:item.name="{ item }">
              <v-tooltip bottom>
                <template v-slot:activator="{on, attrs}">
                  <div v-bind="attrs" class="ma-3" v-on="on">{{ item.name }}</div>
                </template>
                <span>{{ $cvutils.getTranslatedConfigDesc(item.id) }}</span>
              </v-tooltip>
            </template>
            <!-- eslint-disable-next-line vue/valid-v-slot -->
            <template v-slot:item.value="{ item }">
              <v-chip
                v-if="item.type === 'color'"
                :color="item.value"
                text-color="white"
              >
              {{ item.value }}
              </v-chip>
              <div
                v-if="item.type === 'password'"
              >
              <translate>(Write Only)</translate>
              </div>
              <div v-if="item.type === 'image'"
              >
              <v-img
                :src=item.value
                :max-height="$appConfig.customerLogoHeight"
                :max-width="$appConfig.customerLogoWidth"
              >
              </v-img>
              </div>
              <div
                v-if="item.type === 'int'"
              >
              {{ item.value }}
              </div>
              <div
                v-if="item.type === 'string'"
              >
                <v-tooltip top>
                  <template v-slot:activator="{on, attrs}">
                    <span v-bind="attrs" v-on="on">{{ truncate(item.value) }}</span>
                  </template>
                  <span>{{ item.value }}</span>
                </v-tooltip>
              </div>
              <div
                v-if="item.type == 'enum'"
              >
              <!-- use display value for enum's-->
              {{ $cvutils.getTranslatedConfigID(`${item.id}.${item.value}`) }}
              </div>
              <div
                v-if="item.type === 'bool'"
              >
                <div>
                  <v-tooltip top>
                    <template v-slot:activator="{on, attrs}">
                      <v-icon v-if="item.value" v-bind="attrs" v-on="on" color="green">mdi-check-circle-outline</v-icon>
                      <v-icon v-else v-bind="attrs" v-on="on" color="red">mdi-close-circle-outline</v-icon>
                    </template>
                    <span>
                      <translate :key="item.id + '_enabled'" v-if="item.value">Enabled</translate>
                      <translate :key="item.id + '_disabled'" v-else>Disabled</translate>
                    </span>
                  </v-tooltip>
                </div>
              </div>
              <EditableList
                v-if="item.type === 'list'"
                v-model="item.value"
                :readonly="true"
              >
              </EditableList>
              <EditableObjectList
                v-if="item.type === 'object'"
                v-model="item.value"
                :schema="item.schema"
                :config="item.id"
                :readonly="true"
              >
              </EditableObjectList>
            </template>

            <!-- eslint-disable-next-line vue/valid-v-slot -->
            <template v-slot:group.header="{items}">
              <th colspan="4">
                {{ $cvutils.getTranslatedCategoryID(items[0].category) }}
              </th>
            </template>

            <!-- eslint-disable-next-line vue/valid-v-slot -->
            <template v-slot:item.id="{ item }">
              <!-- override the id slot with the action buttons -->
              <v-icon
                small
                class="mr-2"
                @click="editItem(item)"
              >
                mdi-pencil
              </v-icon>
              <v-tooltip
                v-if="changedSettings.findIndex(entry => entry.id === item.id) !== -1"
                bottom
              >
                <template v-slot:activator="{on, attrs}">
                  <v-icon
                    v-on="on"
                    v-bind="attrs"
                    small
                    class="mr-2"
                    @click="revertItem(item)"
                  >
                    mdi-undo
                  </v-icon>
                </template>
                <div><translate>Revert Setting</translate></div>
              </v-tooltip>
            </template>
          </v-data-table>
          <v-card-actions class="pt-0">
            <v-spacer></v-spacer>
            <v-btn
                  color="primary"
                  class="ml-2 mr-2 pa-2"
                  small
                  :disabled="changedSettings.length===0"
                  :loading="saveInProgress"
                  @click="commit"
                >
                  <translate>Save Changes</translate>
                </v-btn>
          </v-card-actions>
        </v-card>
    </v-container>

    <NetworkStatus />
    <NotificationDialog
      v-model="showNotification"
      :notification="notification"
      @close="showNotification = false"
    />
  </v-container>
</template>

<script>
import {translate} from 'vue-gettext';

import EditableList from '@/components/EditableList.vue';
import EditableObjectList from '@/components/EditableObjectList.vue';
import NetworkStatus from '@/components/NetworkStatus';
import NotificationDialog from '@/components/NotificationDialog';

const {gettext: $gettext} = translate;

export default {
  name: 'AdminConfig',

  components: {
    EditableList,
    EditableObjectList,
    NotificationDialog,
    NetworkStatus
  },


  data: () => ({
    selectedItem: {},
    selectedIndex: -1,
    dialog: false,
    showSecret: false,
    configLoading: false,
    saveInProgress: false,
    search: '',
    AUTHM_PRIVATE_KEY: 'private_key', 
    // order of category groups on the admin configuration page.
    categoryOrder: ['app_theme.light',
                    'app_theme.dark',
                    'app_theme.login_gradient',
                    'customer',
                    'feature_flags',
                    'delegation',
                    'oob',
                    'misc',
                    'reports.general_usage',
                    'jira',
                    'device',
                    'ciba',
                    'siem',
                    'idp',
                    'oidc',
                    'oidc.api',
                   ],
    headers: [
      {
        text: "Name",
        align: "start",
        sortable: false,
        value: "name"
      },
      { text: "Value",
        align: "start",
        sortable: false,
        value: "value",
      },
      { text: "Action",
        align: "end",
        sortable: false,
        value: "id", // put id in the action header so we can search on it without displaying it.
      }
    ],
    config: [],
    changedSettings: [],
    authFromConfig: '',

    // Notification dialog settings.
    showNotification: false,
    notification: {
      showAuthBtn: false,
      showReload: false,
      timeout: 5000,
      title: '',
      body: '',
      type: 'info'
    }

  }),
  methods: {
    editItem(item) {
      this.selectedIndex = this.config.indexOf(item);
      this.selectedItem = Object.assign({}, item);
      this.showSecret = false;
      this.dialog = true;
    },
    revertItem(item) {
      let revertIndex = this.config.indexOf(item);
      let deleteIndex = this.changedSettings.findIndex(entry => entry.id === item.id);
      if (deleteIndex !== -1 && revertIndex !== -1) {
        // restore back to original value and delete from changed set.
        this.config[revertIndex].value = this.changedSettings[deleteIndex].orig;
        this.changedSettings.splice(deleteIndex, 1);
      }
    },
    close () {
      this.dialog = false;
      this.selectedIndex = -1;
      this.selectedItem = {};
    },
    truncate(text) {
      let maxlength = 30;
      let truncated = text;
      if (text.length > maxlength) {
        truncated = truncated.substring(0, maxlength) + '...';
      }
      return truncated;
    },
    async getIDPAuthMethod(){
      for(var i=0; i<this.changedSettings.length; i++){
        if(this.changedSettings[i].id == "idp.api_auth_method"){
          return this.changedSettings[i].value;
        }
      }
      // if getting here, have to check what value config is holding
      return this.authFromConfig;
    },
    async encodeClientSecret(){
      for(var i=0; i<this.changedSettings.length; i++){
        if(this.changedSettings[i].id == "idp.api_client_secret"){
          let str = this.changedSettings[i].value;
          let b64 = btoa(str);
          this.changedSettings[i].value = b64;
          return;
        }
      }
      // if getting here, have to check what value config is holding
      return this.authFromConfig;
    },
    async commit() {
      // if we have something to commit, then we can do it.
      if (this.changedSettings.length > 0) {
        // put a loading spinner on the save button.
        this.saveInProgress = true;

        let authMethod = await this.getIDPAuthMethod();
        if( authMethod == this.AUTHM_PRIVATE_KEY){
          // in this case, we need to change the client_secret into base64 encoded
          await this.encodeClientSecret();
        }
        const {status, notification, ids_failed} = await this.$cvApi.adminConfigUpdate(this.changedSettings);

        // if we successfully updated all our settings, then clear out the changed cache.
        if (status && ids_failed.length === 0) {
          this.changedSettings = [];
        }
        // any failures reported back, revert the data table to the original values.
        else if (ids_failed.length > 0) {
          for (let i = 0; i < ids_failed.length; i++) {
            let failed_id = ids_failed[i];
            let failedIndex = this.changedSettings.findIndex(item => item.id === failed_id);
            let dataTableIndex = this.config.findIndex(item => item.id === failed_id);
            if (failedIndex !== -1 && dataTableIndex !== -1) {
              this.config[dataTableIndex].value = this.changedSettings[failedIndex].orig;
            }
          }
          // now clear the changed setting cache.
          this.changedSettings = [];
        }

        // DOC: if status == false and ids_failed.length == 0, then there was a more
        // fundamental issue, could be a timeout, or something more major.  don't touch
        // the state of the UI s that the user can retry the update or decide to abandon
        // it.

        this.saveInProgress = false;

        if (notification) {
          this.notify(notification);
        }

      }
    },
    async update () {
      // only update if we changed something.
      if (this.selectedIndex > -1 && (this.selectedItem.value !== this.config[this.selectedIndex].value)) {

        // if its not present in our settings, then add it.
        let changedIndex = this.changedSettings.findIndex(item => item.id === this.selectedItem.id);
        if (changedIndex === -1) {
          // construct an object to hold this updated setting.
          let updatedSetting = {
            id: this.selectedItem.id,
            value: this.selectedItem.value,
            type: this.selectedItem.type,
            name: this.selectedItem.name,
            orig: this.config[this.selectedIndex].value
          }
          this.changedSettings.push(Object.assign({}, updatedSetting));
        }
        // otherwise we already changed it simply update the value.
        else {
          // if we changed it back to its original value, remove it from the list of changed settings.
          if (this.changedSettings[changedIndex].orig === this.selectedItem.value) {
            let deleteIndex = this.changedSettings.findIndex(item => item.id === this.selectedItem.id);
            this.changedSettings.splice(deleteIndex, 1);
          }
          // otherwise, update its value.
          else {
            this.changedSettings[changedIndex].value = this.selectedItem.value;
            this.changedSettings[changedIndex].type = this.selectedItem.type;
          }
        }

        // if we updated a password item, then clear its value now that we are done
        if (this.config[this.selectedIndex].type === 'password') {
          this.selectedItem.value = '';
        }

        // admin the config UI data table as well.
        Object.assign(this.config[this.selectedIndex], this.selectedItem);
      }
      // close the edit dialog.
      this.close();
    },
    async load() {
      this.configLoading = true;
      this.search = '';
      const {status, notification, settings} = await this.$cvApi.adminConfigList();

      if (status) {
        let tempConfig = settings;
        tempConfig.forEach((setting) => {
          // prefix the category with NN_ where NN is the sort order.
          let categoryPrefix = this.categoryOrder.indexOf(setting.category).toString().padStart(2, "0");
          setting.category = `${categoryPrefix}_${setting.category}`
          if(setting.id == "idp.api_auth_method"){
            // save the auth method while loading to save future efforts
            this.authFromConfig = setting.value;
          }

          // pre-translate the id into the name so that we can filter on it too.
          setting.name = this.$cvutils.getTranslatedConfigID(setting.id);
        })
        this.config = tempConfig;
      }
      else {
        if (notification) {
          this.$emit('notify', notification);
        }
      }
      // stop the config loading indicator.
      this.configLoading = false;
    },
    getRestrictedValues(config, valid_values) {
      let translated_rvs = [];
      valid_values.forEach((rv) => {
        translated_rvs.push({
          display: this.$cvutils.getTranslatedConfigID(`${config}.${rv}`),
          value: rv
        });
      });
      return translated_rvs;
    },
    notify(notification) {
      if (!notification) {
        return;
      }
      this.notification = notification
      this.showNotification = true;
    }
  },
  beforeRouteLeave(to, from, next) {
    let leavePage = true;
    if (this.changedSettings.length > 0) {
      let message = $gettext('You have unsaved changes, are you sure you want to leave?')
      leavePage = window.confirm(message);
    }
    if (leavePage) {
      // clear changes and allow the route change.
      this.changedSettings = [];
      next();
    }
    else {
      // block the route change.
      next(false);
    }
  },
  mounted() {
    this.load();
  }
}
</script>


<style>/**
 * Temporary fix for breaking change in Vuetify v-grid
 * (see : https://github.com/vuetifyjs/vuetify/issues/11408)
 * TODO - remove this after migration
 */
.row:not([class*="my-"]):not([class*="ma-"]):not([class*="mt-"]):not([class*="mb-"]) {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
}</style>
